import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { resolveAssetUrl } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Spinner } from "../../components/Brand";
import { currency } from "../../lib/status";
import { useSEO, useStructuredData } from "../../lib/seo";
import { trackEvent } from "../../lib/analytics";
import { toast } from "sonner";

export default function ServiceDetail() {
    const { id } = useParams();
    const [svc, setSvc] = useState(null);
    const [step, setStep] = useState(1);
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [time, setTime] = useState("10:00");
    const [addrIdx, setAddrIdx] = useState(0);
    const [payment, setPayment] = useState("cash");
    const [coupon, setCoupon] = useState("");
    const [discount, setDiscount] = useState(0);
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);
    const [membership, setMembership] = useState(null);
    const { user, refreshMe } = useAuth();
    const nav = useNavigate();

    useSEO({
        title: svc
            ? `${svc.name} — Book from AED ${svc.base_price} | Luxurio Edge`
            : "Service — Luxurio Edge",
        description: svc
            ? `${svc.description || svc.name}. Book now for AED ${svc.base_price}. ${svc.duration_minutes} min · vetted technicians · transparent pricing.`
            : "Premium on-demand home service in UAE.",
        image: svc?.image_url ? resolveAssetUrl(svc.image_url) : undefined,
        type: "product",
    });

    useStructuredData(
        svc
            ? {
                "@context": "https://schema.org",
                "@type": "Service",
                "name": svc.name,
                "description": svc.description || svc.name,
                "image": svc.image_url ? resolveAssetUrl(svc.image_url) : undefined,
                "provider": {
                    "@type": "Organization",
                    "name": "Luxurio Edge Technical Services",
                    "url": "https://luxurioedgetechnical.com/",
                },
                "areaServed": ["AE"],
                "offers": {
                    "@type": "Offer",
                    "price": String(svc.base_price),
                    "priceCurrency": svc.currency || "AED",
                    "availability": "https://schema.org/InStock",
                },
            }
            : null,
    );

    useEffect(() => {
        (async () => {
            const { data } = await api.get(`/services/${id}`);
            setSvc(data);
        })();
        if (user) {
            api.get("/membership/me").then(({ data }) => setMembership(data)).catch(() => {});
        } else {
            setMembership(null);
        }
    }, [id, user]);

    if (!svc) return <Spinner />;

    const isAnon = !user;
    const requireAuthAndReturn = () => {
        toast.info("Please sign in to continue with your booking");
        nav(`/login?next=${encodeURIComponent(`/customer/services/${id}`)}`);
    };

    const TAX_RATE = 0.05;
    const subtotal = svc.base_price;
    const memberPct = membership?.plan?.discount_percent || 0;
    const memberDiscount = +((subtotal * memberPct) / 100).toFixed(2);
    const totalDiscount = +(discount + memberDiscount).toFixed(2);
    const taxable = Math.max(0, subtotal - totalDiscount);
    const tax = +(taxable * TAX_RATE).toFixed(2);
    const total = +(taxable + tax).toFixed(2);

    const addresses = user?.addresses || [];
    const hasAddress = addresses.length > 0;

    const validateCoupon = async () => {
        if (!coupon) return;
        try {
            const { data } = await api.post("/coupons/validate", {
                code: coupon,
                subtotal,
            });
            setDiscount(data.discount);
            toast.success(`Coupon applied: -${currency(data.discount, "AED")}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Invalid coupon");
            setDiscount(0);
        }
    };

    const submitBooking = async () => {
        if (isAnon) {
            requireAuthAndReturn();
            return;
        }
        if (!hasAddress) {
            toast.error("Please add an address in your profile first");
            return;
        }
        setBusy(true);
        try {
            const scheduled = new Date(`${date}T${time}:00`).toISOString();
            const { data } = await api.post("/bookings", {
                service_id: svc.id,
                address: addresses[addrIdx],
                scheduled_at: scheduled,
                payment_method: payment,
                coupon_code: coupon || undefined,
                notes,
            });
            toast.success(`Booking ${data.booking_number} confirmed!`);
            trackEvent("booking_created", {
                booking_id: data.id,
                service_id: svc.id,
                service_name: svc.name,
                value: data.total_amount,
                currency: data.currency || "AED",
                payment_method: payment,
            });
            if (payment !== "cash") {
                trackEvent("payment_completed", {
                    booking_id: data.id,
                    value: data.total_amount,
                    currency: data.currency || "AED",
                });
            }
            await refreshMe();
            nav(`/customer/bookings/${data.id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Booking failed");
        } finally {
            setBusy(false);
        }
    };

    const addToCart = async () => {
        if (isAnon) {
            requireAuthAndReturn();
            return;
        }
        if (!hasAddress) {
            toast.error("Please add an address in your profile first");
            return;
        }
        setBusy(true);
        try {
            const scheduled = new Date(`${date}T${time}:00`).toISOString();
            await api.post("/cart/items", {
                service_id: svc.id,
                address: addresses[addrIdx],
                scheduled_at: scheduled,
                notes,
            });
            toast.success("Added to cart");
            nav("/customer/cart");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not add to cart");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
                <button
                    onClick={() => nav(-1)}
                    className="text-sm text-[#555] hover:text-[#180A21] mb-4"
                    data-testid="svc-back"
                >
                    ← Back
                </button>
                <div className="aspect-[16/9] overflow-hidden bg-[#F3F2F0] mb-8">
                    {svc.image_url && (
                        <img
                            src={resolveAssetUrl(svc.image_url)}
                            alt={svc.name}
                            className="w-full h-full object-cover"
                        />
                    )}
                </div>
                <div className="lx-label text-[#B2912A] mb-3">
                    {svc.pricing_type} · {svc.duration_minutes} min
                </div>
                <h1 className="font-serif text-4xl text-[#180A21] mb-4">{svc.name}</h1>
                <p className="text-[#555] leading-relaxed mb-8">{svc.description}</p>

                {svc.inclusions?.length > 0 && (
                    <div className="mb-6">
                        <div className="lx-label text-[#555] mb-3">What&apos;s Included</div>
                        <ul className="space-y-2">
                            {svc.inclusions.map((i) => (
                                <li
                                    key={i}
                                    className="text-sm text-[#180A21] flex gap-3 items-start"
                                >
                                    <span className="text-[#D4AF37] mt-0.5">✓</span>
                                    {i}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {svc.exclusions?.length > 0 && (
                    <div className="mb-6">
                        <div className="lx-label text-[#555] mb-3">Not Included</div>
                        <ul className="space-y-2">
                            {svc.exclusions.map((i) => (
                                <li key={i} className="text-sm text-[#555] flex gap-3 items-start">
                                    <span className="text-[#555] mt-0.5">×</span>
                                    {i}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* Booking form */}
            <aside className="lg:col-span-5">
                {isAnon ? (
                    <div className="lx-card p-7 lg:sticky lg:top-24" data-testid="anon-booking-card">
                        <div className="lx-label text-[#B2912A] mb-6">Ready to book?</div>
                        <div className="pb-6 border-b border-[#F3F2F0] mb-6">
                            <div className="flex items-baseline justify-between">
                                <div className="lx-label text-[#555]">From</div>
                                <div className="font-serif text-3xl text-[#180A21]">
                                    {currency(svc.base_price, "AED")}
                                </div>
                            </div>
                            <div className="text-xs text-[#555] mt-2">
                                {svc.duration_minutes} min · {svc.pricing_type} pricing · Tax extra
                            </div>
                        </div>
                        <p className="text-sm text-[#555] leading-relaxed mb-6">
                            Sign in or create a free account to add this to your cart or book instantly.
                            It takes less than 60 seconds.
                        </p>
                        <button
                            type="button"
                            data-testid="anon-book-now"
                            onClick={requireAuthAndReturn}
                            className="lx-btn-gold w-full py-3.5 inline-flex items-center justify-center gap-2 mb-3"
                        >
                            Sign in to book →
                        </button>
                        <button
                            type="button"
                            data-testid="anon-add-to-cart"
                            onClick={requireAuthAndReturn}
                            className="w-full py-3 border border-[#180A21] text-[#180A21] text-sm font-medium hover:bg-[#180A21] hover:text-[#D4AF37] transition"
                        >
                            + Add to cart
                        </button>
                        <div className="text-center text-xs text-[#555] mt-4">
                            New to Luxurio Edge?{" "}
                            <Link
                                to={`/register?next=${encodeURIComponent(`/customer/services/${id}`)}`}
                                data-testid="anon-register-link"
                                className="text-[#D4AF37] hover:underline font-medium"
                            >
                                Create an account
                            </Link>
                        </div>
                    </div>
                ) : (
                <div className="lx-card p-7 lg:sticky lg:top-24">
                    <div className="flex items-center justify-between mb-6">
                        <div className="lx-label text-[#B2912A]">Book this service</div>
                        <div className="text-xs text-[#555]">Step {step} of 3</div>
                    </div>

                    {step === 1 && (
                        <div className="space-y-5">
                            <div>
                                <label className="lx-label text-[#555] mb-2 block">Date</label>
                                <input
                                    data-testid="booking-date-input"
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    min={new Date().toISOString().slice(0, 10)}
                                    className="lx-input"
                                />
                            </div>
                            <div>
                                <label className="lx-label text-[#555] mb-2 block">Time</label>
                                <div className="grid grid-cols-4 gap-2">
                                    {["08:00", "10:00", "12:00", "14:00", "16:00", "18:00", "20:00"].map(
                                        (t) => (
                                            <button
                                                key={t}
                                                type="button"
                                                data-testid={`time-${t}`}
                                                onClick={() => setTime(t)}
                                                className={`py-2 text-sm border transition ${
                                                    time === t
                                                        ? "bg-[#180A21] text-[#D4AF37] border-[#180A21]"
                                                        : "border-[#E5E2DC] text-[#555] hover:border-[#D4AF37]/50"
                                                }`}
                                            >
                                                {t}
                                            </button>
                                        ),
                                    )}
                                </div>
                            </div>
                            <button
                                data-testid="booking-step-next"
                                onClick={() => setStep(2)}
                                className="lx-btn-primary w-full py-3 font-medium"
                            >
                                Next: Address →
                            </button>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-5">
                            <div>
                                <label className="lx-label text-[#555] mb-3 block">
                                    Service Address
                                </label>
                                {!hasAddress && (
                                    <div className="p-4 bg-[#FAFAF8] border border-[#E5E2DC] text-sm text-[#555]">
                                        You have no saved addresses. Add one in{" "}
                                        <a
                                            href="/customer/profile"
                                            className="underline text-[#180A21]"
                                        >
                                            Profile
                                        </a>{" "}
                                        first.
                                    </div>
                                )}
                                {addresses.map((a, i) => (
                                    <button
                                        key={a.id}
                                        type="button"
                                        data-testid={`addr-${i}`}
                                        onClick={() => setAddrIdx(i)}
                                        className={`block w-full text-left p-4 mb-2 border transition ${
                                            addrIdx === i
                                                ? "border-[#D4AF37] bg-[#D4AF37]/5"
                                                : "border-[#E5E2DC] hover:border-[#D4AF37]/50"
                                        }`}
                                    >
                                        <div className="lx-label text-[#B2912A] mb-1">{a.label}</div>
                                        <div className="text-sm text-[#180A21]">{a.line1}</div>
                                        {a.line2 && (
                                            <div className="text-sm text-[#555]">{a.line2}</div>
                                        )}
                                        <div className="text-xs text-[#555] mt-1">
                                            {a.city}, {a.country}
                                        </div>
                                    </button>
                                ))}
                            </div>
                            <textarea
                                data-testid="booking-notes-input"
                                placeholder="Notes for the technician (optional)"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={3}
                                className="lx-input"
                            />
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setStep(1)}
                                    className="flex-1 py-3 border border-[#E5E2DC] text-[#555]"
                                >
                                    ← Back
                                </button>
                                <button
                                    type="button"
                                    data-testid="booking-step-next-2"
                                    onClick={() => setStep(3)}
                                    disabled={!hasAddress}
                                    className="lx-btn-primary flex-1 py-3 disabled:opacity-50"
                                >
                                    Next: Payment →
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-5">
                            <div>
                                <label className="lx-label text-[#555] mb-2 block">
                                    Payment Method
                                </label>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        ["cash", "Cash"],
                                        ["card", "Card"],
                                        ["wallet", "Wallet"],
                                    ].map(([v, l]) => (
                                        <button
                                            key={v}
                                            type="button"
                                            data-testid={`pay-${v}`}
                                            onClick={() => setPayment(v)}
                                            className={`py-2.5 text-sm border ${
                                                payment === v
                                                    ? "bg-[#180A21] text-[#D4AF37] border-[#180A21]"
                                                    : "border-[#E5E2DC] text-[#555]"
                                            }`}
                                        >
                                            {l}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="lx-label text-[#555] mb-2 block">
                                    Coupon Code
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        data-testid="booking-coupon-input"
                                        value={coupon}
                                        onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                                        placeholder="e.g. WELCOME10"
                                        className="lx-input flex-1"
                                    />
                                    <button
                                        type="button"
                                        data-testid="coupon-apply-btn"
                                        onClick={validateCoupon}
                                        className="px-4 py-3 border border-[#180A21] text-[#180A21] text-sm font-medium"
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-[#F3F2F0] space-y-2 text-sm">
                                <Row label="Subtotal" value={currency(subtotal, "AED")} />
                                {discount > 0 && (
                                    <Row
                                        label="Discount"
                                        value={`- ${currency(discount, "AED")}`}
                                    />
                                )}
                                <Row label="Tax (5%)" value={currency(tax, "AED")} />
                                <div className="border-t border-[#F3F2F0] pt-3 flex items-center justify-between">
                                    <div className="lx-label text-[#180A21]">Total</div>
                                    <div className="font-serif text-2xl text-[#180A21]">
                                        {currency(total, "AED")}
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setStep(2)}
                                    className="py-3 px-4 border border-[#E5E2DC] text-[#555] text-sm"
                                >
                                    ← Back
                                </button>
                                <button
                                    type="button"
                                    data-testid="booking-add-to-cart-btn"
                                    onClick={addToCart}
                                    disabled={busy}
                                    className="flex-1 py-3 border border-[#180A21] text-[#180A21] text-sm font-medium hover:bg-[#180A21] hover:text-[#D4AF37] transition disabled:opacity-50"
                                >
                                    + Add to cart
                                </button>
                                <button
                                    type="button"
                                    data-testid="booking-confirm-btn"
                                    onClick={submitBooking}
                                    disabled={busy}
                                    className="lx-btn-gold flex-1 py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {busy ? <Spinner /> : "Book now →"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                )}
            </aside>
            <style>{`.lx-input{width:100%;padding:.7rem 1rem;background:white;border:1px solid #E5E2DC;outline:none;transition:all 200ms ease}.lx-input:focus{border-color:#D4AF37;box-shadow:0 0 0 1px #D4AF37}`}</style>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-[#555]">{label}</span>
            <span className="text-[#180A21] font-medium">{value}</span>
        </div>
    );
}
